import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@dukene/shared';
import { ArrowLeft, User, Mail, Phone, Globe, DollarSign, AlertCircle } from 'lucide-react';

export default function CreateAffiliate() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        // User Information
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',

        // Affiliate Information
        displayName: '',
        bio: '',
        website: '',

        // Social Media
        twitter: '',
        facebook: '',
        instagram: '',
        youtube: '',
        linkedin: '',
        tiktok: '',

        // Payment Information
        paymentEmail: '',
        bankAccount: '',
        bankName: '',
        accountHolderName: '',
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const formatPhoneNumber = (value: string) => {
        const cleaned = value.replace(/\D/g, '');

        if (cleaned.startsWith('254')) {
            return '+' + cleaned;
        } else if (cleaned.startsWith('0')) {
            return '+254' + cleaned.slice(1);
        } else if (cleaned.length > 0 && !cleaned.startsWith('254')) {
            return '+254' + cleaned;
        }

        return '+' + cleaned;
    };

    const validateForm = () => {
        if (!formData.firstName.trim()) {
            setError('First name is required');
            return false;
        }
        if (!formData.lastName.trim()) {
            setError('Last name is required');
            return false;
        }
        if (!formData.email.trim() || !formData.email.includes('@')) {
            setError('Valid email is required');
            return false;
        }
        if (!formData.phoneNumber.trim()) {
            setError('Phone number is required');
            return false;
        }
        if (!formData.displayName.trim()) {
            setError('Display name is required');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!validateForm()) {
            return;
        }

        setLoading(true);

        try {
            const formattedPhone = formatPhoneNumber(formData.phoneNumber);

            // Prepare social media object
            const socialMedia: Record<string, string> = {};
            if (formData.twitter) socialMedia.twitter = formData.twitter;
            if (formData.facebook) socialMedia.facebook = formData.facebook;
            if (formData.instagram) socialMedia.instagram = formData.instagram;
            if (formData.youtube) socialMedia.youtube = formData.youtube;
            if (formData.linkedin) socialMedia.linkedin = formData.linkedin;
            if (formData.tiktok) socialMedia.tiktok = formData.tiktok;

            const payload = {
                // User data
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                phoneNumber: formattedPhone,

                // Affiliate data
                displayName: formData.displayName,
                bio: formData.bio || undefined,
                website: formData.website || undefined,
                socialMedia: Object.keys(socialMedia).length > 0 ? socialMedia : undefined,

                // Payment data
                paymentEmail: formData.paymentEmail || undefined,
                bankAccount: formData.bankAccount || undefined,
                bankName: formData.bankName || undefined,
                accountHolderName: formData.accountHolderName || undefined,
            };

            await apiClient.post('/affiliate/admin/create', payload);

            // Navigate back to affiliates list
            navigate('/affiliates');
        } catch (err: any) {
            setError(err.message || 'Failed to create affiliate');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-8">
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={() => navigate('/affiliates')}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
                >
                    <ArrowLeft className="w-5 h-5" />
                    Back to Affiliates
                </button>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Create New Affiliate</h1>
                <p className="text-gray-600">Add a new affiliate account to the platform</p>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-red-900 text-sm">Error</p>
                            <p className="text-sm text-red-700 mt-1">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* User Information */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <User className="w-5 h-5 text-gray-700" />
                        <h2 className="text-lg font-semibold text-gray-900">User Information</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                                First Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="firstName"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                                Last Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="lastName"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                Email <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-2">
                                Phone Number (WhatsApp) <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="tel"
                                    id="phoneNumber"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleChange}
                                    placeholder="0712345678"
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                    required
                                />
                            </div>
                            <p className="mt-1 text-xs text-gray-500">Used for WhatsApp OTP login</p>
                        </div>
                    </div>
                </div>

                {/* Affiliate Information */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <User className="w-5 h-5 text-gray-700" />
                        <h2 className="text-lg font-semibold text-gray-900">Affiliate Profile</h2>
                    </div>

                    <div className="space-y-6">
                        <div>
                            <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
                                Display Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="displayName"
                                name="displayName"
                                value={formData.displayName}
                                onChange={handleChange}
                                placeholder="e.g., Tech Reviewer Kenya"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                required
                            />
                            <p className="mt-1 text-xs text-gray-500">Public name shown to customers</p>
                        </div>

                        <div>
                            <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                                Bio
                            </label>
                            <textarea
                                id="bio"
                                name="bio"
                                value={formData.bio}
                                onChange={handleChange}
                                rows={3}
                                placeholder="Tell us about yourself..."
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                                Website
                            </label>
                            <div className="relative">
                                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="url"
                                    id="website"
                                    name="website"
                                    value={formData.website}
                                    onChange={handleChange}
                                    placeholder="https://example.com"
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Social Media */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6">Social Media (Optional)</h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="twitter" className="block text-sm font-medium text-gray-700 mb-2">
                                Twitter
                            </label>
                            <input
                                type="text"
                                id="twitter"
                                name="twitter"
                                value={formData.twitter}
                                onChange={handleChange}
                                placeholder="@username"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label htmlFor="facebook" className="block text-sm font-medium text-gray-700 mb-2">
                                Facebook
                            </label>
                            <input
                                type="text"
                                id="facebook"
                                name="facebook"
                                value={formData.facebook}
                                onChange={handleChange}
                                placeholder="facebook.com/username"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label htmlFor="instagram" className="block text-sm font-medium text-gray-700 mb-2">
                                Instagram
                            </label>
                            <input
                                type="text"
                                id="instagram"
                                name="instagram"
                                value={formData.instagram}
                                onChange={handleChange}
                                placeholder="@username"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label htmlFor="youtube" className="block text-sm font-medium text-gray-700 mb-2">
                                YouTube
                            </label>
                            <input
                                type="text"
                                id="youtube"
                                name="youtube"
                                value={formData.youtube}
                                onChange={handleChange}
                                placeholder="youtube.com/@channel"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label htmlFor="linkedin" className="block text-sm font-medium text-gray-700 mb-2">
                                LinkedIn
                            </label>
                            <input
                                type="text"
                                id="linkedin"
                                name="linkedin"
                                value={formData.linkedin}
                                onChange={handleChange}
                                placeholder="linkedin.com/in/username"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label htmlFor="tiktok" className="block text-sm font-medium text-gray-700 mb-2">
                                TikTok
                            </label>
                            <input
                                type="text"
                                id="tiktok"
                                name="tiktok"
                                value={formData.tiktok}
                                onChange={handleChange}
                                placeholder="@username"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                        </div>
                    </div>
                </div>

                {/* Payment Information */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <div className="flex items-center gap-2 mb-6">
                        <DollarSign className="w-5 h-5 text-gray-700" />
                        <h2 className="text-lg font-semibold text-gray-900">Payment Information (Optional)</h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label htmlFor="paymentEmail" className="block text-sm font-medium text-gray-700 mb-2">
                                Payment Email
                            </label>
                            <input
                                type="email"
                                id="paymentEmail"
                                name="paymentEmail"
                                value={formData.paymentEmail}
                                onChange={handleChange}
                                placeholder="payments@example.com"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label htmlFor="bankName" className="block text-sm font-medium text-gray-700 mb-2">
                                Bank Name
                            </label>
                            <input
                                type="text"
                                id="bankName"
                                name="bankName"
                                value={formData.bankName}
                                onChange={handleChange}
                                placeholder="e.g., Equity Bank"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label htmlFor="accountHolderName" className="block text-sm font-medium text-gray-700 mb-2">
                                Account Holder Name
                            </label>
                            <input
                                type="text"
                                id="accountHolderName"
                                name="accountHolderName"
                                value={formData.accountHolderName}
                                onChange={handleChange}
                                placeholder="Full name on account"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                        </div>

                        <div>
                            <label htmlFor="bankAccount" className="block text-sm font-medium text-gray-700 mb-2">
                                Bank Account Number
                            </label>
                            <input
                                type="text"
                                id="bankAccount"
                                name="bankAccount"
                                value={formData.bankAccount}
                                onChange={handleChange}
                                placeholder="Account number"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                            />
                        </div>
                    </div>
                </div>

                {/* Submit Buttons */}
                <div className="flex items-center justify-end gap-4">
                    <button
                        type="button"
                        onClick={() => navigate('/affiliates')}
                        className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {loading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Creating...
                            </>
                        ) : (
                            'Create Affiliate'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
